import hrApi from '@/api/v1/hr';
import assign from 'lodash/assign';
import cloneDeep from 'lodash/cloneDeep';

export default {
  namespaced: true,

  state: {
    developer: {
      personal_data: {
        id: '',
        first_name: '',
        last_name: '',
        email: '',
        city: '',
        nationality: '',
        phone_number: '',
        birthdate: '',
        level: '',
        experience_in_years: '',
        is_verified: false,
        linkedin_link: '',
        git_link: '',
        skype_link: '',
        status: '',
        software_house: {
          name: '',
          pm_first_name: '',
          pm_last_name: '',
          pm_email: '',
          pm_phone_number: '',
        },
      },
      availability: {
        status: '',
        from: '',
        to: '',
      },
      finance: {
        contract_types: [],
        currency: '',
        rate: '',
        rate_with_margin: '',
        working_hours: [],
      },
      basic_data_filled: true,
      attachments: [],
      roles: [],
      top_skills: [],
      skills: [],
      languages: [],
      tools: [],
      management: [],
      technologies: [],
      project_areas: [],
      soft_skills: {},
      hobbies: [],
      education: [],
      experience: [],
      description: '',
      recommendation: '',
      notes: '',
      profile_progress: {
        level: '',
        label: '',
      },
      verification_status: '',
    },

    score: null,

    isDataLoading: false,
    isSubmitProcessing: false,
    isProfileSubmitProcessing: false,
  },

  mutations: {
    CHANGE_DEVELOPER(state, developerData) {
      state.developer = assign(state.developer, developerData);
    },

    CHANGE_SCORE(state, score) {
      state.score = score;
    },

    CHANGE_SUBMIT_STATUS(state, status) {
      state.isSubmitProcessing = status;
    },
    CHANGE_PROFILE_SUBMIT_STATUS(state, status) {
      state.isProfileSubmitProcessing = status;
    },

    CHANGE_LOADING_STATUS(state, status) {
      state.isDataLoading = status;
    },
  },

  actions: {
    fetchDeveloper({ commit }, developerId) {
      commit('CHANGE_LOADING_STATUS', true);

      return new Promise((resolve, reject) => {
        hrApi.developers
          .show(developerId)
          .then((resp) => {
            commit('CHANGE_DEVELOPER', resp.data);
            resolve();
          })
          .catch(() => {
            reject();
          })
          .finally(() => {
            commit('CHANGE_LOADING_STATUS', false);
          });
      });
    },

    createDeveloper({ commit, dispatch }, developerData) {
      commit('CHANGE_SUBMIT_STATUS', true);

      return new Promise((resolve, reject) => {
        hrApi.developers
          .create(developerData)
          .then((resp) => {
            resolve(resp);
          })
          .catch((err) => {
            dispatch('showError', err.response.data.errors, { root: true });
            reject();
          })
          .finally(() => {
            commit('CHANGE_SUBMIT_STATUS', false);
          });
      });
    },

    updateDeveloper({ state, commit, dispatch }, updatedData) {
      commit('CHANGE_SUBMIT_STATUS', true);

      let updatedDeveloper = cloneDeep(state.developer);
      if (updatedData) {
        updatedDeveloper = assign(updatedDeveloper, updatedData);
      }

      return new Promise((resolve, reject) => {
        hrApi.developers
          .update(state.developer.personal_data.id, updatedDeveloper)
          .then((resp) => {
            commit('CHANGE_DEVELOPER', resp.data);
            dispatch('showSuccess', ['Updated'], { root: true });
            resolve();
          })
          .catch((err) => {
            dispatch('showError', err.response.data.errors, { root: true });
            reject();
          })
          .finally(() => {
            commit('CHANGE_SUBMIT_STATUS', false);
          });
      });
    },

    deleteDeveloper({ commit, dispatch }, developerId) {
      commit('CHANGE_PROFILE_SUBMIT_STATUS', true);

      return new Promise((resolve, reject) => {
        hrApi.developers
          .delete(developerId)
          .then((resp) => {
            resolve(resp);
            dispatch('showSuccess', ['Deleted'], { root: true });
          })
          .catch((err) => {
            dispatch('showError', err.response.data.errors, { root: true });
            reject();
          })
          .finally(() => {
            commit('CHANGE_PROFILE_SUBMIT_STATUS', false);
          });
      });
    },

    uploadAttachments({ state, commit, dispatch }, files) {
      commit('CHANGE_SUBMIT_STATUS', true);

      const formData = new FormData();

      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        formData.append('attachments[]', file);
      }

      return new Promise((resolve, reject) => {
        hrApi.attachments
          .create(state.developer.personal_data.id, formData)
          .then(() => {
            dispatch('showSuccess', ['Updated'], { root: true });
            resolve();
          })
          .catch((err) => {
            dispatch('showError', err.response.data.errors, { root: true });
            reject();
          })
          .finally(() => {
            commit('CHANGE_SUBMIT_STATUS', false);
          });
      });
    },

    deleteDeveloperAttachment({ commit, dispatch }, attachmentData) {
      commit('CHANGE_SUBMIT_STATUS', true);

      return new Promise((resolve, reject) => {
        hrApi.attachments
          .delete(attachmentData.developerId, attachmentData.id)
          .then(() => {
            dispatch('showSuccess', ['Deleted'], { root: true });
            resolve();
          })
          .catch((err) => {
            dispatch('showError', err.response.data.errors, { root: true });
            reject();
          })
          .finally(() => {
            commit('CHANGE_SUBMIT_STATUS', false);
          });
      });
    },

    updateDeveloperAttachment({ commit, dispatch }, attachmentData) {
      commit('CHANGE_SUBMIT_STATUS', true);

      return new Promise((resolve, reject) => {
        hrApi.attachments
          .update(attachmentData.developerId, attachmentData.id, attachmentData.client_visibility)
          .then(() => {
            dispatch('showSuccess', ['Visibility updated'], { root: true });
            resolve();
          })
          .catch((err) => {
            dispatch('showError', err.response.data.errors, { root: true });
            reject();
          })
          .finally(() => {
            commit('CHANGE_SUBMIT_STATUS', false);
          });
      });
    },

    fetchDeveloperSurveyAnswers({ commit, dispatch }, developerId) {
      commit('CHANGE_LOADING_STATUS', true);

      return new Promise((resolve, reject) => {
        hrApi.developers
          .survey(developerId)
          .then((resp) => {
            resolve(resp);
          })
          .catch((err) => {
            dispatch('showError', err.response.data.errors, { root: true });
            reject();
          })
          .finally(() => {
            commit('CHANGE_LOADING_STATUS', false);
          });
      });
    },

    generateDeveloperCv({ dispatch }, cvData) {
      return new Promise((resolve, reject) => {
        hrApi.cv
          .show(cvData.developerId, cvData.cvParams)
          .then((resp) => {
            resolve(resp);
          })
          .catch((err) => {
            dispatch('showError', err.response.data.errors, { root: true });
            reject();
          });
      });
    },

    generateDeveloperInvitation({ commit, dispatch }, invitation) {
      commit('CHANGE_PROFILE_SUBMIT_STATUS', true);

      return new Promise((resolve, reject) => {
        hrApi.developers
          .invite(invitation)
          .then((resp) => {
            resolve(resp);
          })
          .catch((err) => {
            dispatch('showError', err.response.data.errors, { root: true });
            reject();
          })
          .finally(() => {
            commit('CHANGE_PROFILE_SUBMIT_STATUS', false);
          });
      });
    },

    generateExistingDeveloperInvitation({ commit, dispatch }, developerId) {
      commit('CHANGE_PROFILE_SUBMIT_STATUS', true);

      return new Promise((resolve, reject) => {
        hrApi.developers
          .inviteExisting(developerId)
          .then((resp) => {
            resolve(resp);
          })
          .catch((err) => {
            dispatch('showError', err.response.data.errors, { root: true });
            reject();
          })
          .finally(() => {
            commit('CHANGE_PROFILE_SUBMIT_STATUS', false);
          });
      });
    },

    fetchDeveloperScore({ commit, dispatch }, recruitmentData) {
      return new Promise((resolve, reject) => {
        hrApi.recruitments
          .score(recruitmentData.recruitmentId, recruitmentData.developerId)
          .then((resp) => {
            commit('CHANGE_SCORE', resp.data.score);
            resolve(resp);
          })
          .catch((err) => {
            dispatch('showError', err.response.data.errors, { root: true });
            reject();
          })
          .finally(() => {
            commit('CHANGE_SUBMIT_STATUS', false);
          });
      });
    },
  },
};
